import { AppFlowActions, EMITTER_CONSTANTS } from '@/shared/config';
import { auth } from '@/shared/lib';
import emitter from '@/shared/lib/eventEmitter';
import type { SocketEventListener } from './registry';
import {
  addQueueECGEventUpdate,
  addQueueHandleUpdateOverview,
  addQueueHandleUpdateReport,
  addQueueRoomUsers,
} from './queues';

// The 24 registered handlers, moved verbatim from the socket god-class (bodies unchanged,
// including the console.log lines; each still emits the same EMITTER_CONSTANTS value).

/**
 * Records which other users are viewing the same event or report room. Logs the payload and hands it to the room-users queue, which flushes EVENT_ROOM_USER and REPORT_ROOM_USER one second later.
 *
 * @param msg - Socket payload carrying the room type and its current viewers.
 * @returns Nothing.
 */
export const roomUsersListener = (msg: any) => {
  console.log('socket io - roomUsersListener', msg);
  addQueueRoomUsers(msg);
};

/**
 * Forces this client out when the server kicks every session holding the current token. Logs the payload and emits LOGOUT_REQUEST with the reason 'request', ignoring the message contents.
 *
 * @param msg - Socket payload naming the kicked token; only logged.
 * @returns Nothing.
 */
export const kickClientsByTokenListener = (msg: any) => {
  console.log('socket io - kickClientsByTokenListener', msg);
  emitter.emit(AppFlowActions.LOGOUT_REQUEST, 'request');
};

/**
 * Forces this client out when the server kicks every session belonging to the current username. Logs the payload and emits LOGOUT_REQUEST with the reason 'request', ignoring the message contents.
 *
 * @param msg - Socket payload naming the kicked username; only logged.
 * @returns Nothing.
 */
export const kickClientsByUsernameListener = (msg: any) => {
  console.log('socket io - kickClientsByUsernameListener', msg);
  emitter.emit(AppFlowActions.LOGOUT_REQUEST, 'request');
};

/**
 * Relays a server notification that a new session was opened for this account. Logs the payload and re-emits it on the app emitter as NEW_SESSION.
 *
 * @param msg - Socket payload describing the new session.
 * @returns Nothing.
 */
export const newSessionListener = (msg: any) => {
  console.log('socket io - newSessionListener', msg);
  emitter.emit(EMITTER_CONSTANTS.NEW_SESSION, msg);
};

/**
 * Relays a change to the platform maintenance window. Logs the payload and re-emits it on the app emitter as MAINTENANCE_CHANGED.
 *
 * @param msg - Socket payload carrying the maintenance flag and affected client names.
 * @returns Nothing.
 */
export const maintenanceChangedListener = (msg: any) => {
  console.log('socket io - maintenanceChangedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.MAINTENANCE_CHANGED, msg);
};

// Room deviceId
/**
 * Propagates a server-side report change to the report UI and to the batched refresh queues. Logs the payload, re-emits REPORT_UPDATED, and for call-center users queues a reportUpdated entry on the one-second report-reminder batch plus, for supervisors and QA leaders, on the fifteen-second overview batch.
 *
 * @param msg - Socket payload describing the changed report.
 * @returns Nothing.
 */
export const reportUpdatedListener = (msg: any) => {
  console.log('socket io - reportUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.REPORT_UPDATED, msg);
  if (auth.isCallCenter()) {
    addQueueHandleUpdateReport({ type: 'reportUpdated', data: msg });

    if (auth.isCallCenterSupervisor() || auth.isCallCenterQaLeader()) {
      addQueueHandleUpdateOverview({ type: 'reportUpdated', data: msg });
    }
  }
};

/**
 * Relays a server notification that a study group changed. Logs the payload and re-emits it on the app emitter as GROUP_UPDATED.
 *
 * @param msg - Socket payload describing the updated group.
 * @returns Nothing.
 */
export const groupUpdatedListener = (msg: any) => {
  console.log('socket io - groupUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.GROUP_UPDATED, msg);
};

/**
 * Propagates a server-side ECG event change to the report UI and to the batched refresh queues. Logs the payload, queues the study id on the five-second total-events batch when the message carries one, re-emits UPDATE_EVENT_ECG, and for call-center users also queues an eventUpdated entry on the one-second report-reminder batch.
 *
 * @param msg - Socket payload for the changed ECG event, optionally carrying its study.
 * @returns Nothing.
 */
export const eventUpdatedListener = (msg: any) => {
  console.log('socket io - eventUpdatedListener', msg);
  if (msg.study?._id) {
    addQueueECGEventUpdate(msg.study?._id);
  }
  emitter.emit(EMITTER_CONSTANTS.UPDATE_EVENT_ECG, msg);
  if (auth.isCallCenter()) {
    addQueueHandleUpdateReport({ type: 'eventUpdated', data: msg });
  }
};

// Room general
/**
 * Relays a server notification that a study profile has finished generating. Logs the payload and re-emits it on the app emitter as PROFILE_READY.
 *
 * @param msg - Socket payload naming the ready profile.
 * @returns Nothing.
 */
export const profileReadyListener = (msg: any) => {
  console.log('socket io - profileReadyListener', msg);
  emitter.emit(EMITTER_CONSTANTS.PROFILE_READY, msg);
};

// Room profile
/**
 * Relays a server notification that a Holter profile changed. Logs the payload and re-emits it on the app emitter as HOLTER_PROFILE_UPDATED.
 *
 * @param msg - Socket payload describing the updated Holter profile.
 * @returns Nothing.
 */
export const holterProfileUpdatedListener = (msg: any) => {
  console.log('socket io - holterProfileUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.HOLTER_PROFILE_UPDATED, msg);
};

/**
 * Relays a server notification that beat data for a study changed. Logs the payload and re-emits it on the app emitter as BEATSUPDATED_EVENT.
 *
 * @param msg - Socket payload describing the changed beats.
 * @returns Nothing.
 */
export const beatsUpdatedListener = (msg: any) => {
  console.log('socket io - beatsUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.BEATSUPDATED_EVENT, msg);
};

// Wraps beatsUpdatedListener the same way connectListener's inline registration did.
/**
 * Takes the raw beatsUpdated socket frame before the rest of the app sees it. Forwards the payload to beatsUpdatedListener, which re-emits it as BEATSUPDATED_EVENT, then refreshes the cached socket id through auth.getSocketId.
 *
 * @param data - Raw socket frame for the beatsUpdated event.
 * @returns Nothing.
 */
const beatsUpdatedHandler = (data: any) => {
  // phase - beatsUpdated
  beatsUpdatedListener(data);
  auth.getSocketId();
};

/**
 * Relays a server notification that ECG events for a study changed. Logs the payload and re-emits it on the app emitter as EVENTSUPDATED_EVENT.
 *
 * @param msg - Socket payload describing the changed ECG events.
 * @returns Nothing.
 */
export const eventsUpdatedListener = (msg: any) => {
  console.log('socket io - eventsUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.EVENTSUPDATED_EVENT, msg);
};

// Wraps eventsUpdatedListener the same way connectListener's inline registration did.
/**
 * Takes the raw eventsUpdated socket frame before the rest of the app sees it. Forwards the payload to eventsUpdatedListener, which re-emits it as EVENTSUPDATED_EVENT, then refreshes the cached socket id through auth.getSocketId.
 *
 * @param data - Raw socket frame for the eventsUpdated event.
 * @returns Nothing.
 */
const eventsUpdatedHandler = (data: any) => {
  eventsUpdatedListener(data);
  auth.getSocketId();
};

/**
 * Relays a server notification that a study's daily summary was recalculated. Logs the payload and re-emits it on the app emitter as DAILY_SUMMARY_UPDATED.
 *
 * @param msg - Socket payload describing the updated daily summary.
 * @returns Nothing.
 */
export const dailySummaryUpdatedListener = (msg: any) => {
  console.log('socket io - dailySummaryUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.DAILY_SUMMARY_UPDATED, msg);
};

/**
 * Relays a server notification that a study's AFib heart-rate summary was recomputed. Logs the payload and re-emits it on the app emitter as AFIB_HR_SUMMARY_UPDATED.
 *
 * @param msg - Socket payload describing the updated AFib heart-rate summary.
 * @returns Nothing.
 */
export const afibHrSummaryUpdatedListener = (msg: any) => {
  console.log('socket io - afibHrSummaryUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.AFIB_HR_SUMMARY_UPDATED, msg);
};

/**
 * Relays a server notification that technician comments on a study changed. Logs the payload and re-emits it on the app emitter as TECHNICIAN_COMMENT_UPDATED.
 *
 * @param msg - Socket payload carrying the updated technician comments.
 * @returns Nothing.
 */
export const technicianCommentsUpdatedListener = (msg: any) => {
  console.log('socket io - technicianCommentsUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.TECHNICIAN_COMMENT_UPDATED, msg);
};

/**
 * Relays a server notification that a study's RR heat map changed. Logs the payload and re-emits it on the app emitter as RR_HEATMAP_UPDATED.
 *
 * @param msg - Socket payload describing the updated RR heat map.
 * @returns Nothing.
 */
export const rrHeatMapUpdatedListener = (msg: any) => {
  console.log('socket io - rrHeatMapUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.RR_HEATMAP_UPDATED, msg);
};

/**
 * Relays a server notification that RR heat-map cells were marked reviewed. Logs the payload and re-emits it on the app emitter as RR_CELL_REVIEWED.
 *
 * @param msg - Socket payload naming the reviewed RR cells.
 * @returns Nothing.
 */
export const rrCellsReviewedListener = (msg: any) => {
  console.log('socket io - rrCellsReviewedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.RR_CELL_REVIEWED, msg);
};

/**
 * Relays progress reported by a long-running server command on a study. Logs the payload and re-emits it on the app emitter as COMMAND_PROGRESS_UPDATED.
 *
 * @param msg - Socket payload carrying the command's progress.
 * @returns Nothing.
 */
export const commandProgressUpdatedListener = (msg: any) => {
  console.log('socket io - commandProgressUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.COMMAND_PROGRESS_UPDATED, msg);
};

/**
 * Relays a server notification that a study's minutely heart rate was updated. Logs the payload and re-emits it on the app emitter as MINUTELY_HR_UPDATED.
 *
 * @param msg - Socket payload describing the updated minutely heart rate.
 * @returns Nothing.
 */
export const minutelyHrUpdatedListener = (msg: any) => {
  console.log('socket io - minutelyHrUpdatedListener', msg);
  emitter.emit(EMITTER_CONSTANTS.MINUTELY_HR_UPDATED, msg);
};

/**
 * Relays a server notification that the AI pipeline stopped processing a study. Logs the payload and re-emits it on the app emitter as AI_STOP_PROCESSING.
 *
 * @param msg - Socket payload describing the halted AI processing job.
 * @returns Nothing.
 */
export const aiStopProcessingListener = (msg: any) => {
  console.log('socket io - aiStopProcessingListener', msg);
  emitter.emit(EMITTER_CONSTANTS.AI_STOP_PROCESSING, msg);
};

/**
 * Relays a server-side migration of AI data from version one to version two. Logs the payload and re-emits it on the app emitter as MIGRATE_AI_DATA_VER1_TO_VER2.
 *
 * @param msg - Socket payload describing the migration.
 * @returns Nothing.
 */
export const migrateAiDataListener = (msg: any) => {
  console.log('socket io - migrateAiDataListener', msg);
  emitter.emit(EMITTER_CONSTANTS.MIGRATE_AI_DATA_VER1_TO_VER2, msg);
};

/**
 * Relays a server instruction to stop an in-progress save. Logs the payload and re-emits it on the app emitter as STOP_SAVING.
 *
 * @param msg - Socket payload describing the save being stopped.
 * @returns Nothing.
 */
export const stopSavingListener = (msg: any) => {
  console.log('socket io - stopSavingListener', msg);
  emitter.emit(EMITTER_CONSTANTS.STOP_SAVING, msg);
};

/**
 * Delivers an inbox message pushed by the server. Logs the payload and re-emits it on the app emitter as INBOX.
 *
 * @param msg - Socket payload carrying the inbox message.
 * @returns Nothing.
 */
export const inboxListener = (msg: any) => {
  console.log('socket io - inboxListener', msg);
  emitter.emit(EMITTER_CONSTANTS.INBOX, msg);
};

/**
 * Relays a server request to re-capture the automatically detected events. Logs the payload and re-emits it on the app emitter as RE_CAPTURE_AUTO_EVENTS.
 *
 * @param msg - Socket payload describing the re-capture request.
 * @returns Nothing.
 */
export const recaptureAutoEventsListener = (msg: any) => {
  console.log('socket io - recaptureAutoEventsListener', msg);
  emitter.emit(EMITTER_CONSTANTS.RE_CAPTURE_AUTO_EVENTS, msg);
};

// Same order as the god-class registered them, so removal there stays reviewable against this.
export const socketListeners: SocketEventListener[] = [
  { event: 'roomUsers', handler: roomUsersListener },
  { event: 'KickClientsByToken', handler: kickClientsByTokenListener },
  { event: 'KickClientsByUsername', handler: kickClientsByUsernameListener },
  { event: 'newSession', handler: newSessionListener },
  { event: 'maintenanceChanged', handler: maintenanceChangedListener },
  { event: 'reportUpdated', handler: reportUpdatedListener },
  { event: 'groupUpdated', handler: groupUpdatedListener },
  { event: 'eventUpdated', handler: eventUpdatedListener },
  { event: 'profileReady', handler: profileReadyListener },
  { event: 'holterProfileUpdated', handler: holterProfileUpdatedListener },
  { event: 'beatsUpdated', handler: beatsUpdatedHandler },
  { event: 'eventsUpdated', handler: eventsUpdatedHandler },
  { event: 'dailySummaryUpdated', handler: dailySummaryUpdatedListener },
  { event: 'afibHrSummaryUpdated', handler: afibHrSummaryUpdatedListener },
  { event: 'technicianCommentsUpdated', handler: technicianCommentsUpdatedListener },
  { event: 'rrHeatMapUpdated', handler: rrHeatMapUpdatedListener },
  { event: 'rrCellsReviewed', handler: rrCellsReviewedListener },
  { event: 'commandProgressUpdated', handler: commandProgressUpdatedListener },
  { event: 'minutelyHrUpdated', handler: minutelyHrUpdatedListener },
  { event: 'aiStopProcessing', handler: aiStopProcessingListener },
  { event: 'migrateAiData', handler: migrateAiDataListener },
  { event: 'stopSaving', handler: stopSavingListener },
  { event: 'inbox', handler: inboxListener },
  { event: 'recaptureAutoEvents', handler: recaptureAutoEventsListener },
];
